import { ResponseBody } from './ResponseBody';
import './DeepSearchCard.css';

interface DeepSearchSource {
  n: number;
  title: string;
  url: string;
  domain: string;
}

interface DeepSearchBody {
  answer?: unknown;
  queries?: unknown;
  sources?: unknown;
  provider?: unknown;
}

function narrowSources(v: unknown): DeepSearchSource[] {
  if (!Array.isArray(v)) return [];
  return v.filter(
    (s): s is DeepSearchSource =>
      !!s &&
      typeof s === 'object' &&
      typeof (s as { n?: unknown }).n === 'number' &&
      typeof (s as { title?: unknown }).title === 'string' &&
      typeof (s as { url?: unknown }).url === 'string' &&
      typeof (s as { domain?: unknown }).domain === 'string',
  );
}

function narrowQueries(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  return v.filter((q): q is string => typeof q === 'string' && q.trim().length > 0);
}

/** A section heading, either style: `## Less certain` or `**Less certain**`.
 *  Both appear in the wild — the prompt asks for `##`, but models drift to bold,
 *  and older answers in a reloaded session still use bold. */
function isHeading(line: string): boolean {
  const l = line.trim();
  return /^#{1,4}\s+\S/.test(l) || /^(?:\*\*|__)[^*_]+(?:\*\*|__)\s*:?\s*$/.test(l);
}

function isSourcesHeading(line: string): boolean {
  return /^(?:#{1,4}\s+|\*\*|__)?\s*sources\s*:?\s*(?:\*\*|__)?$/i.test(line.trim());
}

/**
 * Drop the answer's own "**Sources**" block.
 *
 * The synth prompt asks the model to end with a Sources list, and we ALSO get
 * the same sources as structured data — so both rendered, and the reply showed
 * "Sources" twice: once as plain "[n] Title — url" text, then again as the real
 * linked list. The structured one is strictly better (clickable, deduped,
 * carries the domain), so the prose copy is what goes.
 *
 * Stops at the next bold heading rather than running to the end: "Less certain"
 * is emitted AFTER Sources, and it's the honest-uncertainty line — the last
 * thing we'd want to silently eat.
 */
export function stripSourcesSection(md: string): string {
  const lines = md.split('\n');
  const start = lines.findIndex(isSourcesHeading);
  if (start < 0) return md;
  let end = lines.length;
  for (let i = start + 1; i < lines.length; i += 1) {
    const l = lines[i] ?? '';
    if (isHeading(l) && !isSourcesHeading(l)) { end = i; break; }
  }
  return [...lines.slice(0, start), ...lines.slice(end)]
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export const DEEP_SEARCH_ACTION = 'brew_deep_search';

/**
 * A Deep Search answer.
 *
 * Renders as a NORMAL chat reply — same markdown, same type, same rhythm as
 * every other answer — with a mode tag and its sources. It used to be a bordered,
 * shadowed card at 13px, which read as a widget bolted into the conversation
 * rather than Brew answering you. Deep Search is a MODE, not a different kind of
 * thing, so the only lasting visual difference is the tag that names the mode and
 * the receipts underneath it.
 */
export function DeepSearchCard({ body }: { body: unknown }) {
  const b = (body && typeof body === 'object' ? body : {}) as DeepSearchBody;
  const answer = typeof b.answer === 'string' ? b.answer : '';
  const sources = narrowSources(b.sources);
  const queries = narrowQueries(b.queries);

  if (!answer && sources.length === 0) return null;

  // The structured `sources` below replace the model's prose list.
  const prose = sources.length > 0 ? stripSourcesSection(answer) : answer;

  return (
    <div className="ds">


      {prose ? <ResponseBody body={prose} /> : null}

      {sources.length > 0 ? (
        <div className="ds__sources">
          <div className="ds__sources-head">
            {sources.length} {sources.length === 1 ? 'source' : 'sources'}
          </div>
          <ol className="ds__list">
            {sources.map((s) => (
              <li key={`${s.n}-${s.url}`} className="ds__item">
                <span className="ds__n" aria-hidden="true">{s.n}</span>
                <a className="ds__link" href={s.url} target="_blank" rel="noreferrer">
                  <span className="ds__title">{s.title}</span>
                  <span className="ds__domain">{s.domain}</span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      ) : null}

      {/* Collapsed by default: what Brew searched is a "show your working"
          detail, useful when an answer looks off and noise when it doesn't. */}
      {queries.length > 0 ? (
        <details className="ds__queries">
          <summary>Research queries</summary>
          <ul>
            {queries.map((q, i) => <li key={`${i}-${q}`}>{q}</li>)}
          </ul>
        </details>
      ) : null}
    </div>
  );
}